import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { TableSummaryCalculatorFunction } from "@app/directives/_layout/v-table/_components/visualisations/table-summary-box/table-summary-box.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { AccountStatus } from "@app/enum/account/AccountStatus";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest } from "rxjs";
import { AccountSubscriptionType } from "../../../_enums/AccountSubscriptionType";
import { IAccountSubscription } from "../../../_models/IAccountSubscription";
import { AccountService } from "../../../_services/account.service";
import { CustomerSubscriptionService } from "../../../_services/customer-subscriptions.service";
import { FoundationVehiclesService } from "../../../_services/vehicle.service";
import "./customer-subscriptions.component.scss";

@UntilDestroy()
@Component({
    selector: "customer-subscriptions",
    template: require("./customer-subscriptions.component.html")
})
export class CustomerSubscriptionsComponent implements OnInit, OnChanges {
    @Input() accountId: number;

    tabCount: number;

    loading = false;
    subscriptions: IAccountSubscription[];
    AccountSubscriptionType = AccountSubscriptionType;

    tableColumns: IVTableColumn<IAccountSubscription>[];
    tableRows: IVTableRow[];
    filterTabs: IFilterBarTab<IAccountSubscription>[] = [];

    filterEvent: IAggregateFilterEvent<IAccountSubscription>;
    totalCharge = 0;
    activeSubscriptions = 0;

    constructor(
        private readonly accountService: AccountService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly customerSubscriptionService: CustomerSubscriptionService,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService
    ) {
        this.createTabs();
    }

    ngOnInit(): void {
        this.createTable();
        this.loadData();
    }

    ngOnChanges(): void {
    }

    onFilterChanged(event: IAggregateFilterEvent<IAccountSubscription>): void {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;
    }

    uniqueCustomerCalculator: TableSummaryCalculatorFunction<IAccountSubscription> = (item, allItems) => {
        return allItems.indexOf(item) ? 1 : 0;
    }

    subscriptionsCalculator: TableSummaryCalculatorFunction<IAccountSubscription> = (item) => {
        return item.active ? 1 : 0;
    }

    monthlyChargeCalculator: TableSummaryCalculatorFunction<IAccountSubscription> = (item) => {
        return item.ChargePerUnit * item.Amount;
    }

    yearlyChargeCalculator: TableSummaryCalculatorFunction<IAccountSubscription> = (item) => {
        return item.ChargePerUnit * item.Amount * 12;
    }

    // ---

    private loadData(): void {
        this.loading = true;

        let subscriptionsObservable = this.customerSubscriptionService.getSubscriptions();

        if (!isNaN(this.accountId)) {
            subscriptionsObservable = this.customerSubscriptionService.getSubscriptionsForAccount(this.accountId);
        }

        combineLatest([
            this.accountService.getCustomerAccounts(),
            subscriptionsObservable,
            this.vehicleService.getVehicles(true)
        ]).pipe(
            untilDestroyed(this)           
        ).subscribe({
            next: ([accounts, subscriptions, vehicles]) => {
                this.subscriptions = subscriptions;
                this.customerSubscriptionService.setEndTimes(this.subscriptions, accounts);
                this.totalCharge = this.customerSubscriptionService.calculateSubscriptionCosts(this.subscriptions, vehicles);
                this.activeSubscriptions = this.subscriptions.filter(s => s.active).length;
                this.createTableRows();
                this.loading = false;
            }
        });
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ minTableWidth: 1024, sorted: false }),
                createCellFunction: sub => this.vTableService.createStatusCell(sub.status)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CustomerSubscriptions.CreatedTime", sorted: true, sortDirection: SortDirection.Descending, minTableWidth: 1300 }),
                createCellFunction: sub => this.vTableService.createDateCell(new Date(sub.CreatedTime), { dateTime: false })
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CustomerSubscriptions.StartTime", minTableWidth: 1200 }),
                createCellFunction: sub => this.vTableService.createDateCell(new Date(sub.StartTime), { dateTime: false })
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CustomerSubscriptions.EndTime" }),
                createCellFunction: sub => this.vTableService.createDateCell(new Date(sub.EndTime), { dateTime: false })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CustomerSubscriptions.Customer" }),
                createCellFunction: sub => this.vTableService.createTableCell(sub.account.CompanyName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CustomerSubscriptions.Type", minTableWidth: 700, width: 160 }),
                createCellFunction: sub => this.vTableService.createEnumCell(AccountSubscriptionType, "AccountSubscriptionType", sub.Type)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CustomerSubscriptions.Quantity", sortType: SortType.Number, minTableWidth: 800, width: 100 }),
                createCellFunction: sub => this.vTableService.createTableCell(sub.Amount?.toLocaleString(), { sortData: sub.Amount })
            },
            {
                header: this.vTableService.createTableHeader({ title: "Charge per Unit", sortType: SortType.Number, minTableWidth: 1000, width: 130 }),
                createCellFunction: sub => this.vTableService.createTableCell("£" + sub.ChargePerUnit?.toLocaleString(), { sortData: sub.ChargePerUnit })
            },
            {
                header: this.vTableService.createTableHeader({ title: "Total Charge", sortType: SortType.Number, minTableWidth: 600, width: 120 }),
                createCellFunction: sub => this.vTableService.createTableCell("£" + sub.TotalCharge?.toLocaleString(), { sortData: sub.TotalCharge })
            }
        ];
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.subscriptions.filter(f => f.account.Status == AccountStatus.Active), this.tableColumns);
        this.tabCount = this.tableRows.length;
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition(this.translationService.t("CustomerSubscriptions.Active"), a => a.active);
        this.createTabDefinition(this.translationService.t("CustomerSubscriptions.Expired"), a => !a.active);
    }

    private createTabDefinition(title: string, callback: (account: IAccountSubscription) => boolean) {

        this.filterTabs.push({ title: title, predicate: callback });
    }
}
